import styled from '@emotion/styled';

import { SecondaryButton } from '@/components/common/Buttons';
import { Card } from '@/components/common/Card';
import { DateRangeLabel } from '@/components/common/DateRangeLabel';
import { Icon } from '@/components/common/Icon';
import { Image } from '@/components/common/image';
import { Heading, Paragraph } from '@/components/common/MarkUp';
import { dateInFuture } from '@/lib/dates';
import { captionLarge } from '@/styles/typography';

interface EventCardProps {
  title?: string;
  image?: any;
  shortDescription?: string;
  locations?: string[];
  startsAtDate?: string;
  endsAtDate?: string;
  startsAtTime?: string;
  endsAtTime?: string;
  locale?: string;
  eventPageUrl?: string;
  eventType?: any;
  onDemand?: boolean;
  ctaTextOverride?: string;
  fallbackImage: any;
  timeZone?: string;
  trackingTag?: { blockName: string; blockAction: string };
}

export const EventCard = ({
  title,
  image,
  shortDescription,
  locations,
  startsAtDate,
  endsAtDate,
  startsAtTime,
  endsAtTime,
  locale = 'en-GB',
  eventPageUrl,
  eventType,
  onDemand,
  ctaTextOverride,
  fallbackImage,
  timeZone,
  trackingTag,
}: EventCardProps) => {
  const eventImage =
    (image?.asset?._ref && image) ||
    (fallbackImage?.asset?._ref && fallbackImage);

  return (
    <StyledEventCard
      header={
        <EventCardThumb>
          {!!eventImage && (
            <Image
              isDecorativeImage={eventImage?.isDecorativeImage}
              {...eventImage}
            />
          )}
        </EventCardThumb>
      }
      zoomOnHover
      borderRadius="m"
      padding={600}
    >
      <EventCardBody>
        <EventType>{eventType?.title}</EventType>
        <Heading className="EventTitle" size="subheadline-bold" title={title}>
          {title}
        </Heading>
        <EventCardParagraph size="body-md" title={shortDescription}>
          {shortDescription}
        </EventCardParagraph>
        <EventCardFooter>
          <EvetCardDetailsList>
            <li>
              <Icon icon="LocationPin" size="15px" />

              <span>
                {locations[0]} {locations[1] && ` & ${locations[1]}`}
              </span>
            </li>
            <li>
              {dateInFuture(new Date(endsAtDate)) && (
                <>
                  <Icon icon="Calendar" size="15px" />
                  <DateRangeLabel
                    startsAtDate={startsAtDate}
                    endsAtDate={endsAtDate}
                    startsAtTime={startsAtTime}
                    endsAtTime={endsAtTime}
                    locale={locale}
                    timeZone={timeZone}
                  />
                </>
              )}
            </li>
          </EvetCardDetailsList>
          {eventPageUrl && (
            <SecondaryButton
              componentTheme="light"
              className={`event-card-cta ${!onDemand ? 'primary' : ''} ${
                trackingTag?.blockName
                  ? `tracking-${trackingTag.blockName}__${trackingTag.blockAction}`
                  : ''
              }`}
              href={eventPageUrl}
            >
              {ctaTextOverride ||
                (onDemand ? 'See details' : 'See details & register')}
            </SecondaryButton>
          )}
        </EventCardFooter>
      </EventCardBody>
    </StyledEventCard>
  );
};

const StyledEventCard = styled(Card)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const EventCardThumb = styled.div`
  img {
    object-fit: cover;
    height: 100%;
  }
`;

const EventCardBody = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  gap: var(--spacing-small);
`;

const EventType = styled.div`
  ${captionLarge}
  font-weight: var(--font-weight-semi-bold);
  text-transform: uppercase;
`;

const EventCardParagraph = styled(Paragraph)`
  margin-bottom: var(--spacing-large);
`;

const EventCardFooter = styled.div`
  margin-top: auto;

  .event-card-cta {
    width: 100%;
    text-align: center;
    justify-content: center;
    background-color: var(--action-inverse-strong);
  }

  .event-card-cta:hover {
    background-color: var(--surface-inverse-primary);
    color: var(--text-inverse-strong);
  }
`;

const EvetCardDetailsList = styled.ul`
  li:not(:first-of-type) {
    margin-top: var(--spacing-small);
  }

  li {
    display: flex;
  }

  li > span {
    margin-left: 0.25em;
  }

  margin-bottom: var(--spacing-medium);
`;
